import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import FondoPantallaConfig from './FondoPantallaConfig';
import FuenteInterfazConfig from './FuenteInterfazConfig';

const CuentaConfig = () => {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [profesiones, setProfesiones] = useState([]);
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    profesionId: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const initializeData = async () => {
      try {
        await Promise.all([
          loadUserInfo(),
          loadProfesiones()
        ]);
        
        // Cargar estadísticas si el usuario es Personal
        if (user?.rol === 'PERSONAL') {
          loadUserStats();
        }
      } catch (error) {
        console.error('Error inicializando datos:', error);
      }
    };

    initializeData();
  }, [user]);

  const loadUserInfo = async () => {
    try {
      const response = await api.get('/user/me');
      setUserInfo(response.data.user);
      setFormData({
        nombre: response.data.user.nombre || '',
        email: response.data.user.email || '',
        profesionId: response.data.user.profesionId?.toString() || ''
      });
    } catch (error) {
      console.error('Error cargando información del usuario:', error);
      // No mostrar error crítico que pueda cerrar la sesión
      if (error.response?.status === 401) {
        console.warn('Error de autenticación en loadUserInfo');
      }
    }
  };

  const loadProfesiones = async () => {
    try {
      const response = await api.get('/profesiones');
      setProfesiones(response.data);
    } catch (error) {
      console.error('Error cargando profesiones:', error);
      // No mostrar error crítico para profesiones
    }
  };

  const loadUserStats = async () => {
    try {
      const response = await api.get('/user/me/stats');
      setUserStats(response.data);
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
      // No mostrar error crítico para estadísticas
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await api.patch('/user/me', formData);
      setUserInfo(response.data.user);
      updateUser(response.data.user);
      setSuccess('Perfil actualizado correctamente');
    } catch (err) {
      setError('Error al actualizar perfil: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Validaciones
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        throw new Error('Las contraseñas no coinciden');
      }

      if (passwordData.newPassword.length < 6) {
        throw new Error('La nueva contraseña debe tener al menos 6 caracteres');
      }

      await api.patch('/user/me/password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setSuccess('Contraseña cambiada correctamente');
    } catch (err) {
      setError('Error al cambiar contraseña: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!userInfo) {
    return (
      <div className="text-center">
        <div className="spinner-border" role="status">
          <span className="sr-only">Cargando información de usuario...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h3>⚙️ Mi Cuenta</h3>
      
      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
        </div>
      )}

      <div className="row">
        {/* Información del perfil */}
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h5>📝 Información del Perfil</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleUpdateProfile}>
                <div className="form-group mb-3">
                  <label htmlFor="nombre" className="form-label">Nombre</label>
                  <input
                    type="text"
                    id="nombre"
                    name="nombre"
                    className="form-control"
                    value={formData.nombre}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group mb-3">
                  <label htmlFor="email" className="form-label">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="form-control"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group mb-3">
                  <label htmlFor="profesionId" className="form-label">Profesión</label>
                  <select
                    id="profesionId"
                    name="profesionId"
                    className="form-control"
                    value={formData.profesionId}
                    onChange={handleInputChange}
                  >
                    <option value="">Seleccionar profesión</option>
                    {profesiones.map(prof => (
                      <option key={prof.id} value={prof.id}>{prof.nombre}</option>
                    ))}
                  </select>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Actualizando...' : 'Actualizar Perfil'}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Cambiar contraseña */}
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h5>🔒 Cambiar Contraseña</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleChangePassword}>
                <div className="form-group mb-3">
                  <label htmlFor="currentPassword" className="form-label">Contraseña Actual</label>
                  <input
                    type="password"
                    id="currentPassword"
                    name="currentPassword"
                    className="form-control"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                </div>

                <div className="form-group mb-3">
                  <label htmlFor="newPassword" className="form-label">Nueva Contraseña</label>
                  <input
                    type="password"
                    id="newPassword"
                    name="newPassword"
                    className="form-control"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    minLength="6"
                    required
                  />
                  <small className="form-text text-muted">
                    Mínimo 6 caracteres
                  </small>
                </div>

                <div className="form-group mb-3">
                  <label htmlFor="confirmPassword" className="form-label">Confirmar Nueva Contraseña</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    className="form-control"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-warning"
                  disabled={loading}
                >
                  {loading ? 'Cambiando...' : 'Cambiar Contraseña'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Información adicional */}
      <div className="row mt-4">
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h5>ℹ️ Información de la Cuenta</h5>
            </div>
            <div className="card-body">
              <p><strong>Usuario:</strong> {userInfo.nombre}</p>
              <p><strong>Email:</strong> {userInfo.email}</p>
              <p><strong>Rol:</strong> 
                <span className={`badge ${
                  userInfo.rol === 'JEFE' ? 'badge-danger' :
                  userInfo.rol === 'ADMIN' ? 'badge-warning' :
                  'badge-success'
                }`}>
                  {userInfo.rol}
                </span>
              </p>
              <p><strong>Profesión:</strong> {userInfo.profesion?.nombre || 'No asignada'}</p>
              <p><strong>Cuenta creada:</strong> {userInfo.createdAt ? new Date(userInfo.createdAt).toLocaleDateString() : 'No disponible'}</p>
            </div>
          </div>
        </div>

        {/* Estadísticas del usuario (solo para Personal) */}
        {userInfo.rol === 'PERSONAL' && (
          <div className="col-md-6">
            <div className="card">
              <div className="card-header">
                <h5>📊 Mis Estadísticas</h5>
                <button 
                  className="btn btn-sm btn-outline-primary"
                  onClick={loadUserStats}
                >
                  <i className="fas fa-sync-alt"></i> Actualizar
                </button>
              </div>
              <div className="card-body">
                {userStats ? (
                  <div>
                    <div className="row text-center">
                      <div className="col-6">
                        <h4 className="text-primary">{userStats.partesAsignadas}</h4>
                        <small>Partes Asignadas</small>
                      </div>
                      <div className="col-6">
                        <h4 className="text-success">{userStats.partesCompletadas}</h4>
                        <small>Completadas</small>
                      </div>
                    </div>
                    <div className="row text-center mt-3">
                      <div className="col-6">
                        <h4 className="text-warning">{userStats.partesPendientes}</h4>
                        <small>Pendientes</small>
                      </div>
                      <div className="col-6">
                        <h4 className="text-info">{userStats.partesEnProgreso}</h4>
                        <small>En Progreso</small>
                      </div>
                    </div>
                    <div className="mt-3">
                      <div className="progress">
                        <div 
                          className="progress-bar bg-success" 
                          style={{width: `${userStats.porcentajeCompletado}%`}}
                        >
                          {userStats.porcentajeCompletado.toFixed(1)}%
                        </div>
                      </div>
                      <small className="text-muted">Progreso general</small>
                    </div>
                    <div className="mt-2">
                      <p className="mb-1"><strong>Expedientes con partes:</strong> {userStats.expedientesConPartes}</p>
                      <p className="mb-0"><strong>Notificaciones no leídas:</strong> {userStats.notificacionesNoLeidas}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-muted">Haz clic en "Actualizar" para ver tus estadísticas</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <FuenteInterfazConfig
        userInfo={userInfo}
        setUserInfo={setUserInfo}
        setError={setError}
        setSuccess={setSuccess}
      />

      <FondoPantallaConfig
        userInfo={userInfo}
        setUserInfo={setUserInfo}
        setError={setError}
        setSuccess={setSuccess}
      />
    </div>
  );
};

export default CuentaConfig;


